import React from 'react';
import { useTranslation } from 'react-i18next';

import { fromDateTime, TaskActivityStatusColor } from '@/services/ws-funnel/src/main/resources/rest-api';
import { Badge, Tooltip } from '@/js/libs/ui/src/V2';

import { DATE_TIME_FORMAT } from 'src/constants';
import type { ColumnProps } from '../../../types';
import { MessageAndDateWrapper, TooltipWrapper } from './styled';

export const ActivityCell: React.FC<ColumnProps> = React.memo(function ActivityCell({ item }) {
  const [t] = useTranslation(['backend', 'common', 'serviceDashboard']);

  const title = React.useMemo(() => `+${item.activitiesCount}`, [item.activitiesCount]);

  const tooltip = React.useMemo(() => {
    const type = item.activity?.activityType;
    const translatedType = type ? t(type, { ns: 'common' }) : null;
    const byCreator = item.activity?.creatorName ? ` ${t('serviceDashboard:BY_USER')} ${item.activity.creatorName}` : '';
    let message: string;

    switch (item.activityStatusColor) {
      case TaskActivityStatusColor.GREEN: {
        message = translatedType
          ? `${t('serviceDashboard:GREEN_TASK_MESSAGE_WITH_TYPE', { type: translatedType })}`
          : t('serviceDashboard:GREEN_TASK_MESSAGE');
        break;
      }
      case TaskActivityStatusColor.YELLOW: {
        message = translatedType
          ? `${t('serviceDashboard:YELLOW_TASK_MESSAGE_WITH_TYPE', { type: translatedType })}`
          : t('serviceDashboard:YELLOW_TASK_MESSAGE');
        break;
      }
      case TaskActivityStatusColor.RED: {
        message = translatedType
          ? `${t('serviceDashboard:RED_TASK_MESSAGE_WITH_TYPE', { type: translatedType })}`
          : t('serviceDashboard:RED_TASK_MESSAGE');
        break;
      }
      default:
        message = '';
    }

    return (
      <TooltipWrapper>
        {`${message}${byCreator}`}
        <MessageAndDateWrapper>
          <div>{item.activity?.message}</div>
          <div>{fromDateTime(item.activity?.creationDate)?.local().format(DATE_TIME_FORMAT)}</div>
        </MessageAndDateWrapper>
      </TooltipWrapper>
    );
  }, [item.activity, item.activityStatusColor, t]);

  if (!item.activity || !item.activitiesCount) {
    return null;
  }

  return (
    <Tooltip trigger='hover' placement='top' tooltip={tooltip}>
      <Badge
        title={title}
        positive={item.activityStatusColor === TaskActivityStatusColor.GREEN}
        warning={item.activityStatusColor === TaskActivityStatusColor.YELLOW}
        critical={item.activityStatusColor === TaskActivityStatusColor.RED}
        dataTestId={`${item.activityStatusColor}-badge`}
      />
    </Tooltip>
  );
});
